import { ApolloQueryResult, useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import { navigate } from 'gatsby';
import {
	addIndex,
	any,
	complement,
	curry,
	equals,
	find,
	forEach,
	forEachObjIndexed,
	includes,
	or,
	propEq,
	values,
} from 'ramda';
import React, { FC, useContext, useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useDeepCompareEffect } from 'react-use';
import styled from 'styled-components';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import StyledLink from '@ui/atoms/StyledLink';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import Checkbox from '@ui/forms/Checkbox';
import Dropzone from '@ui/forms/Dropzone';
import PhoneTextInput from '@ui/forms/PhoneTextInput';
import TextInput from '@ui/forms/TextInput';

import NewInputWrapper, { INewInputWrapperProps } from '@ui/molecules/NewInputWrapper';
import SubmitButton, { ISubmitButtonProps } from '@ui/molecules/SubmitButton';

import PhotoUpload from '@ui/organisms/PhotoUpload';

import aligned from '@ui/styled/Aligned';
import fullWidth from '@ui/styled/FullWidth';

import { uneditableFieldView } from '@helpers/Dashboard';

import { commonLabels } from '@utils/button';
import { ApiEndpoints } from '@utils/endpoints';
import { dropzoneErrors } from '@utils/error';
import { doesNotExist, exists } from '@utils/general';

import { getAuthToken } from '@providers/AuthProvider';
import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import pronounOptions from '@data/pronouns';

import {
	IAccountInfoMutationData,
	IAccountInfoMutationResponse,
	IMemberData,
	IMemberPhoneNumberUpdateMutationResponse,
} from '@folx-types/member';

import ACCOUNT_INFO_MUTATION from '@graphql/mutations/AccountInfo.mutation.gql';
import MEMBER_PHONE_NUMBER_UPDATE from '@graphql/mutations/MemberPhoneNumberUpdate.mutation.gql';

import theme from '@theme/FolxTheme';

import { HAS_OTHER_PRONOUNS, OTHER, PRONOUNS, schema, smsSchema } from './AccountInfoEditStep.data';

export interface IAccountEditInfoContent {
	pageHeader: string;
	profilePictureLabel: string;
	firstNameLabel: string;
	firstNamePlaceholder: string;
	lastNameLabel: string;
	lastNamePlaceholder: string;
	pronounsLabel: string;
	emailLabel: string;
	emailDisclaimer: string;
	phoneNumberLabel: string;
	phoneNumberDisclaimer: string;
	phoneNumberPlaceholder: string;
	innerspaceLabel: string;
	innerspacePlaceholder: string;
	updateAccountErrorText: string;
}

interface IAccountInfoEditStepProps {
	pageContent: IAccountEditInfoContent;
	loading?: boolean;
	member?: IMemberData;
	noAccountName?: boolean;
	selfie?: string;
	selfieError?: Error;
	smsEnrolled?: boolean;
}

const PageHeading = styled(HeadingText)`
	margin-bottom: ${theme.spacing.md};
`;

const ProfileDropzoneWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: ${theme.spacing.sm};
	${Dropzone} {
		height: 15rem;
		width: 15rem;
	}
`;

const AlignedLink = aligned(StyledLink);
const AlignedColumn = aligned(FlexColumn);
const FullWidthColumn = fullWidth(FlexColumn);
const FullWidthSubmitButton = fullWidth<ISubmitButtonProps>(SubmitButton);
const FullWidthInputWrapper = fullWidth<INewInputWrapperProps>(NewInputWrapper);

const AccountInfoEditStep: FC<IAccountInfoEditStepProps> = ({
	member,
	pageContent,
	selfie = '',
	selfieError,
	smsEnrolled = true,
}) => {
	const [submitting, setSubmitting] = useState(false);
	const [accountError, setAccountError] = useState<unknown>(null);
	const [showSelfieError, setShowSelfieError] = useState(false);
	const [arePronounsSelected, setArePronounsSelected] = useState(false);

	const {
		pageHeader,
		profilePictureLabel,
		firstNameLabel,
		firstNamePlaceholder,
		lastNameLabel,
		lastNamePlaceholder,
		pronounsLabel,
		emailLabel,
		emailDisclaimer,
		phoneNumberLabel,
		phoneNumberDisclaimer,
		phoneNumberPlaceholder,
		innerspaceLabel,
		innerspacePlaceholder,
		updateAccountErrorText,
	} = pageContent;

	type Schema = Partial<z.input<typeof schema>>;
	type SmsSchema = Partial<z.input<typeof smsSchema>>;

	const form = useForm<Schema | SmsSchema>({
		resolver: zodResolver(smsEnrolled ? schema : smsSchema),
		mode: 'onChange',
	});

	const {
		control,
		watch,
		register,
		setValue,
		handleSubmit,
		setFocus,
		reset,
		resetField,
		formState: { errors, isSubmitted },
	} = form;

	const otherChecked = watch(HAS_OTHER_PRONOUNS);
	const otherPronouns = watch(`${PRONOUNS}.${OTHER}`);
	const checkedPronouns = watch(PRONOUNS);

	const { fields, append, remove, update } = useFieldArray({
		name: PRONOUNS,
		control: control,
		shouldUnregister: true,
	});

	useDeepCompareEffect(() => {
		if (doesNotExist(member)) {
			return;
		}

		reset({
			smsNumber: member?.smsNumber,
			affirmingFirstName: member?.affirmingFirstName,
			affirmingLastName: member?.affirmingLastName,
			nickname: member?.nickname,
		});
	}, [member]);

	useEffect(() => {
		if (doesNotExist(selfie)) {
			return;
		}

		resetField('selfieImage', { defaultValue: selfie });
	}, [selfie]);

	useEffect(() => {
		setFocus('affirmingFirstName');
	}, [setFocus]);

	// Submission logic
	const { client: graphQLv2Context } = useContext(GraphQLv2Context);

	const [memberPhoneNumberUpdate, { error: memberPhoneNumberUpdateError }] =
		useMutation<IMemberPhoneNumberUpdateMutationResponse>(MEMBER_PHONE_NUMBER_UPDATE, {
			client: graphQLv2Context,
		});

	const [updateAccountInfo, { error: updateAccountError }] =
		useMutation<IAccountInfoMutationResponse>(ACCOUNT_INFO_MUTATION);

	const updateSelfieImage = async (selfieImage: string) => {
		const blob = await (await fetch(selfieImage)).blob();

		const authorizationToken = await getAuthToken();

		try {
			await axios.put(`${ApiEndpoints.memberPortal}/image/selfieImage`, blob, {
				headers: {
					authorizationToken,
					'Content-Type': blob.type,
				},
			});
		} catch (error) {
			console.error(`Oof. We've run into a problem™ updating your selfie. ${e}`);
			throw error;
		}
	};

	const updateMemberAccountInfo = async (input: IAccountInfoMutationData) => {
		try {
			await updateAccountInfo({
				variables: input,
			});
		} catch (error) {
			console.error(`Oof. We've run into a problem™ updating your information. ${error}`);
			throw error;
		}
	};

	const saveAccountInfo = async (formData: IAccountInfoMutationData['patch']) => {
		if (!member?.id) {
			throw new Error('Invalid user');
		}
		await updateMemberAccountInfo({
			clientMutationId: member?.id,
			memberId: member?.id,
			patch: formData,
		});
	};

	const updateMemberPhoneNumber = async (newPhoneNumber: string) => {
		if (!member?.id) {
			throw new Error('Invalid user');
		}
		await memberPhoneNumberUpdate({
			variables: {
				clientMutationId: member?.id,
				memberId: member?.id,
				newPhoneNumber,
			},
		});
	};

	const onSubmit = async (data: Record<keyof SmsSchema, unknown>) => {
		if (doesNotExist(data?.selfieImage) && doesNotExist(selfieError)) {
			setShowSelfieError(true);
			return;
		}

		const notEqual = curry((second) => complement(equals(second)));
		const hasNewSelfie = exists(data?.selfieImage) && notEqual(selfie)(data.selfieImage);
		let newData = {};

		const shouldUpdateSmsNumber =
			!smsEnrolled && exists(data?.smsNumber) && notEqual(member?.smsNumber)(data?.smsNumber);

		forEachObjIndexed(async (value, key: keyof SmsSchema) => {
			if (key === 'selfieImage' || key === HAS_OTHER_PRONOUNS || key === 'smsNumber') {
				return;
			}
			const memberKey = member?.[key];
			const dataKey = data?.[key];
			const isNotEqual = notEqual(memberKey)(dataKey);
			newData = isNotEqual ? { ...newData, [key]: dataKey } : newData;
		})(data);

		let promises: Promise<ApolloQueryResult<IAccountInfoMutationResponse> | void>[] = [];
		promises = exists(newData) ? promises.concat(saveAccountInfo(newData)) : promises;
		promises = hasNewSelfie
			? promises.concat(updateSelfieImage(data.selfieImage as string))
			: promises;
		promises = shouldUpdateSmsNumber
			? promises.concat(updateMemberPhoneNumber(data.smsNumber as string))
			: promises;

		if (doesNotExist(promises)) {
			return;
		}

		setSubmitting(true);
		try {
			await Promise.all(promises);
		} catch (error) {
			console.log(error);
			setAccountError(error);
			return;
		} finally {
			setSubmitting(false);
		}

		navigate('..');
	};

	// Pronoun logic
	const updatePronounState = async () => {
		const anyPronounsChecked = any((v: boolean | string) =>
			or(equals<boolean | string>('true')(v), equals<boolean | string>(true)(v))
		);
		const pronounValues = values(checkedPronouns);
		const pronounsSelected = anyPronounsChecked(pronounValues) || exists(otherPronouns);
		setArePronounsSelected(pronounsSelected);
	};

	useDeepCompareEffect(() => {
		updatePronounState();
	}, [{ ...checkedPronouns }, otherPronouns]);

	useEffect(() => remove(), []);

	useEffect(() => {
		if (!otherChecked) {
			setValue(`${PRONOUNS}.${OTHER}`, '');
		}
	}, [otherChecked]);

	useDeepCompareEffect(() => {
		const pronounValues = member?.preferredPronouns ?? [];

		const forEachIndexed = addIndex(forEach);
		forEachIndexed<string, number, string[]>((pronoun, index) => {
			const cannotFind = complement(find<Record<string, string>>(propEq('name', pronoun)));
			const value = includes(pronoun, pronounValues);
			console.log('fields:', fields);
			cannotFind(fields)
				? append({ value, name: pronoun })
				: update(index, { value, name: pronoun });
		}, pronounOptions);

		const otherPronouns = find((pronoun: string) => {
			const doesNotInclude = complement(includes(pronoun));
			return doesNotInclude(pronounOptions);
		}, pronounValues);

		updatePronounState();

		if (exists(otherPronouns)) {
			setValue(HAS_OTHER_PRONOUNS, true);
			setValue(`${PRONOUNS}.${OTHER}`, otherPronouns);
		}
	}, [pronounOptions, member?.preferredPronouns]);

	return (
		<FullWidthColumn>
			<PageHeading>{pageHeader}</PageHeading>
			<form onSubmit={handleSubmit(onSubmit)}>
				<AlignedColumn gap="lg" alignItems="flex-start">
					<ProfileDropzoneWrapper>
						<BasicText variant="field">{profilePictureLabel}</BasicText>
						<PhotoUpload
							imageType="Selfie"
							variant="row"
							previousUpload={selfie}
							hideDelete
							handleUpload={(upload) => {
								setValue('selfieImage', upload, { shouldValidate: true });
							}}>
							{showSelfieError && (
								<BasicText color="red" tint="darken40">
									{dropzoneErrors.required}
								</BasicText>
							)}
						</PhotoUpload>
						<input type="hidden" {...register('selfieImage')} />
					</ProfileDropzoneWrapper>
					<TextInput
						label={firstNameLabel}
						placeholder={firstNamePlaceholder}
						error={!!errors.affirmingFirstName}
						errorLabel={errors?.affirmingFirstName?.message ?? ''}
						{...register('affirmingFirstName')}
					/>
					<TextInput
						label={lastNameLabel}
						placeholder={lastNamePlaceholder}
						error={!!errors.affirmingLastName}
						errorLabel={errors?.affirmingLastName?.message ?? ''}
						{...register('affirmingLastName')}
					/>
					<FullWidthColumn gap="smmd">
						<FullWidthInputWrapper
							title={pronounsLabel}
							titleType="field"
							error={
								isSubmitted && !(arePronounsSelected || otherChecked) && errors?.[PRONOUNS]?.message
							}>
							{exists(fields) &&
								fields.map((field: { id: string; name?: string; value?: boolean }) => (
									<Checkbox
										{...register(`${PRONOUNS}.${field?.name ?? ''}`, {
											value: field?.value,
										})}
										key={field?.id}
										label={field?.name ?? ''}
									/>
								))}
							<Checkbox {...register(HAS_OTHER_PRONOUNS)} label={'Something Else'} />
							{otherChecked && (
								<TextInput
									error={isSubmitted && !arePronounsSelected}
									errorLabel={!arePronounsSelected && errors?.[PRONOUNS]?.message}
									{...register(`${PRONOUNS}.${OTHER}`)}
								/>
							)}
						</FullWidthInputWrapper>
					</FullWidthColumn>
					<FullWidthColumn gap="sm">
						{uneditableFieldView(emailLabel, emailDisclaimer, member?.email)}
					</FullWidthColumn>
					<FullWidthColumn gap="sm">
						{smsEnrolled ? (
							uneditableFieldView(phoneNumberLabel, phoneNumberDisclaimer, member?.smsNumber)
						) : (
							<Controller
								name="smsNumber"
								control={control}
								render={({ field }) => (
									<PhoneTextInput
										placeholder={phoneNumberPlaceholder}
										label={phoneNumberLabel}
										error={isSubmitted && !!errors?.smsNumber}
										errorLabel={errors?.smsNumber?.message}
										type="tel"
										{...field}
									/>
								)}
							/>
						)}
					</FullWidthColumn>
					<TextInput
						label={innerspaceLabel}
						placeholder={innerspacePlaceholder}
						error={!!errors.nickname}
						errorLabel={errors?.nickname?.message ?? ''}
						{...register('nickname')}
					/>
					<FullWidthSubmitButton
						label={commonLabels.save}
						variant="secondary"
						submitting={submitting}
					/>
					<AlignedLink alignSelf="center" variant="underline" to="..">
						{commonLabels.cancel}
					</AlignedLink>
					{(exists(updateAccountError) ||
						exists(accountError) ||
						exists(memberPhoneNumberUpdateError)) && (
						<WYSIWYGContent
							color="red"
							tint="darken40"
							dangerouslySetInnerHTML={{ __html: updateAccountErrorText }}
						/>
					)}
				</AlignedColumn>
			</form>
		</FullWidthColumn>
	);
};

export default AccountInfoEditStep;
